// Re-skin shadcn de packages/hce/src/hce/dictado/BotonDictado.tsx — misma
// lógica (MediaRecorder → POST /api/hce/dictado), solo cambia la UI
// (lucide-react en vez de Tabler, Radix Tooltip en vez de Mantine).
import { Loader2, Mic, MicOff, Square } from 'lucide-react';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getConfig } from '../lib/config';
import { cabecerasBoveda } from '../lib/audit/auditedFetch';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';

type Estado = 'inactivo' | 'grabando' | 'transcribiendo' | 'error';

export interface BotonDictadoProps {
  readonly onTranscrito: (texto: string) => void;
  readonly disabled?: boolean;
}

// getUserMedia/fetch lanzan sus errores en inglés (nombre de DOMException o
// mensaje crudo del navegador) — acá se traduce lo que sabemos reconocer;
// lo demás (p. ej. errores del propio backend, que ya vienen en español) se
// muestra tal cual.
function mensajeDeErrorEnEspanol(err: unknown): string {
  const nombre = (err as { name?: string })?.name;
  const MAPA_NOMBRE: Record<string, string> = {
    NotAllowedError: 'permiso de micrófono denegado',
    PermissionDeniedError: 'permiso de micrófono denegado',
    NotFoundError: 'no se encontró ningún micrófono',
    NotReadableError: 'el micrófono está en uso por otra aplicación o falló el hardware',
    SecurityError: 'el sitio no tiene un contexto seguro (HTTPS) para usar el micrófono',
    AbortError: 'se interrumpió el acceso al micrófono',
    EncodingError: 'no se pudo procesar el audio grabado',
  };
  if (nombre && MAPA_NOMBRE[nombre]) return MAPA_NOMBRE[nombre];
  const mensaje = (err as Error)?.message || String(err);
  if (/permission denied/i.test(mensaje)) return 'permiso de micrófono denegado';
  if (/failed to fetch|network/i.test(mensaje)) return 'no se pudo conectar con el servidor';
  return mensaje;
}

function bytesABase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let binario = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binario += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binario);
}

// Scaleway solo documenta soporte para wav/mp3/flac/mpga/oga/ogg — el
// MediaRecorder del navegador graba en audio/webm (Chrome, códec Opus) o
// audio/ogg (Firefox), ninguno garantizado. Enviar webm tal cual hacía que
// Scaleway lo "transcribiera" como silencio y alucinara texto de relleno
// (p. ej. "Gracias.", un artefacto conocido de Whisper con audio vacío/mal
// decodificado). Se decodifica acá con la Web Audio API nativa (sin
// librerías) y se reempaqueta como WAV PCM 16-bit mono, formato que
// Scaleway sí soporta explícitamente.
// Por debajo de este pico de amplitud (escala -1..1) se considera que no
// hay señal real — Whisper no devuelve un error en ese caso, alucina texto
// de relleno tipo "Gracias." (confirmado mandándole silencio real a
// Scaleway). Mejor cortar acá con un mensaje accionable que gastar la
// llamada y mostrarle al médico un texto que él nunca dijo.
const UMBRAL_SILENCIO = 0.01;

async function blobAWav(blob: Blob): Promise<{ blob: Blob; silencioso: boolean }> {
  const arrayBuffer = await blob.arrayBuffer();
  const AudioContextCtor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const audioCtx = new AudioContextCtor();
  try {
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
    const { buffer, pico } = codificarWav(audioBuffer);
    return { blob: new Blob([buffer], { type: 'audio/wav' }), silencioso: pico < UMBRAL_SILENCIO };
  } finally {
    void audioCtx.close();
  }
}

function codificarWav(audioBuffer: AudioBuffer): { buffer: ArrayBuffer; pico: number } {
  const frecuenciaMuestreo = audioBuffer.sampleRate;
  const longitud = audioBuffer.length;
  const monoDatos = new Float32Array(longitud);
  let pico = 0;
  for (let canal = 0; canal < audioBuffer.numberOfChannels; canal++) {
    const canalDatos = audioBuffer.getChannelData(canal);
    for (let i = 0; i < longitud; i++) monoDatos[i] += canalDatos[i] / audioBuffer.numberOfChannels;
  }
  for (let i = 0; i < longitud; i++) pico = Math.max(pico, Math.abs(monoDatos[i]));

  const buffer = new ArrayBuffer(44 + longitud * 2);
  const view = new DataView(buffer);
  const escribirCadena = (offset: number, cadena: string): void => {
    for (let i = 0; i < cadena.length; i++) view.setUint8(offset + i, cadena.charCodeAt(i));
  };

  escribirCadena(0, 'RIFF');
  view.setUint32(4, 36 + longitud * 2, true);
  escribirCadena(8, 'WAVE');
  escribirCadena(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // 1 canal (mono)
  view.setUint32(24, frecuenciaMuestreo, true);
  view.setUint32(28, frecuenciaMuestreo * 2, true); // byte rate (mono, 16-bit)
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits por muestra
  escribirCadena(36, 'data');
  view.setUint32(40, longitud * 2, true);

  let offset = 44;
  for (let i = 0; i < longitud; i++, offset += 2) {
    const muestra = Math.max(-1, Math.min(1, monoDatos[i]));
    view.setInt16(offset, muestra < 0 ? muestra * 0x8000 : muestra * 0x7fff, true);
  }

  return { buffer, pico };
}

export function BotonDictado({ onTranscrito, disabled }: BotonDictadoProps): JSX.Element {
  const { t } = useTranslation();
  const [estado, setEstado] = useState<Estado>('inactivo');
  const [enLinea, setEnLinea] = useState(navigator.onLine);
  const [errorMensaje, setErrorMensaje] = useState('');
  const grabadorRef = useRef<MediaRecorder | null>(null);
  const trozosRef = useRef<Blob[]>([]);

  useEffect(() => {
    const marcarEnLinea = () => setEnLinea(true);
    const marcarSinLinea = () => setEnLinea(false);
    window.addEventListener('online', marcarEnLinea);
    window.addEventListener('offline', marcarSinLinea);
    return () => {
      window.removeEventListener('online', marcarEnLinea);
      window.removeEventListener('offline', marcarSinLinea);
    };
  }, []);

  async function empezarGrabacion(): Promise<void> {
    setEstado('grabando');
    setErrorMensaje('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      trozosRef.current = [];
      const grabador = new MediaRecorder(stream);
      grabador.ondataavailable = (e) => {
        if (e.data.size > 0) trozosRef.current.push(e.data);
      };
      grabador.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        void enviarATranscribir(grabador.mimeType || 'audio/webm');
      };
      grabadorRef.current = grabador;
      grabador.start();
    } catch (err) {
      setErrorMensaje(mensajeDeErrorEnEspanol(err));
      setEstado('error');
    }
  }

  function detenerGrabacion(): void {
    setEstado('transcribiendo');
    grabadorRef.current?.stop();
  }

  async function enviarATranscribir(mimeType: string): Promise<void> {
    try {
      const blobOriginal = new Blob(trozosRef.current, { type: mimeType });
      const { blob: blobWav, silencioso } = await blobAWav(blobOriginal);
      if (silencioso) throw new Error('El audio grabado no tiene sonido — revisá que el micrófono correcto esté seleccionado y sin silenciar en tu navegador/sistema.');
      const bytes = new Uint8Array(await blobWav.arrayBuffer());
      const config = getConfig();
      const res = await fetch(`${config.vaultUrl}/api/hce/dictado`, {
        method: 'POST',
        headers: cabecerasBoveda(),
        body: JSON.stringify({ audioBase64: bytesABase64(bytes), mimeType: 'audio/wav' }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      onTranscrito(typeof json.texto === 'string' ? json.texto : '');
      setEstado('inactivo');
    } catch (err) {
      setErrorMensaje(mensajeDeErrorEnEspanol(err));
      setEstado('error');
    }
  }

  if (!enLinea) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button type="button" size="icon" variant="ghost" disabled className="size-8">
            <MicOff className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('dictado.sinConexion')}</TooltipContent>
      </Tooltip>
    );
  }

  if (estado === 'grabando') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button type="button" size="icon" variant="destructive" className="size-8" onClick={detenerGrabacion}>
            <Square className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('dictado.detener')}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={estado === 'error' ? 'size-8 text-destructive' : 'size-8'}
          disabled={disabled || estado === 'transcribiendo'}
          onClick={() => void empezarGrabacion()}
        >
          {estado === 'transcribiendo' ? <Loader2 className="size-4 animate-spin" /> : <Mic className="size-4" />}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{estado === 'error' ? `${t('dictado.errorReintentar')}${errorMensaje ? `: ${errorMensaje}` : ''}` : t('dictado.dictar')}</TooltipContent>
    </Tooltip>
  );
}
